import { useZoneProfileStore } from '../store/zoneProfileStore';
import { useMaestroStore } from '../store/maestroStore';
import { formatPlaybackQualityLabel, readBitDepth, readSampleRate } from '../utils/audioQuality';
import { getPlaybackNetworkFormatLabel } from '../utils/playbackNetworkFormatLabel';
import {
  classifyZoneFormatBadge,
  isUntestedDefaultZoneCap,
  UNTESTED_ZONE_BADGE_TEXT,
  type ZoneFormatBadgeKind,
} from '../utils/zoneFormatCeiling';
import { deliveredPcmFormat } from '../utils/rendererRateCap';
import type { ZoneProfile } from './zoneService';
export interface StreamFormat {
  sampleRate: number;
  bitDepth: number;
  formatId: number;
}

export interface StreamDecision {
  action: 'passthrough' | 'downsample' | 'unsupported';
  originalUrl: string;
  effectiveUrl: string;
  originalFormat: StreamFormat;
  targetFormat: { sampleRate: number; bitDepth: number };
  reason: string;
  zoneName: string | null;
  wasDownsampled: boolean;
}

function passthrough(
  url: string, rate: number, depth: number, fmtId: number,
  reason: string, zoneName: string | null = null,
): StreamDecision {
  return {
    action: 'passthrough',
    originalUrl: url,
    effectiveUrl: url,
    originalFormat: { sampleRate: rate, bitDepth: depth, formatId: fmtId },
    targetFormat: { sampleRate: rate, bitDepth: depth },
    reason,
    zoneName,
    wasDownsampled: false,
  };
}

/** Format a sample rate for display. Values < 1000 are already in kHz; values >= 1000 are Hz. */
function fmtRate(rate: number): string {
  return rate >= 1000 ? `${rate / 1000}kHz` : `${rate}kHz`;
}

function getActiveZoneProfile(): { renderer: any; profile: ZoneProfile | null } {
  const renderer = useMaestroStore.getState().activeConnectorRenderer;
  if (!renderer) return { renderer: null, profile: null };
  const profiles = useZoneProfileStore.getState().zones;
  const profile = profiles.find((p) => p.zone_id === renderer.id) || null;
  return { renderer, profile };
}

/**
 * Evaluate a stream against the active zone's capabilities and decide
 * whether to pass through or downsample.
 */
export function routeStream(
  streamUrl: string,
  sampleRate: number,
  bitDepth: number,
  formatId: number,
): StreamDecision {
  const { renderer, profile } = getActiveZoneProfile();

  if (!renderer) {
    return passthrough(streamUrl, sampleRate, bitDepth, formatId,
      'No active renderer — passthrough');
  }

  const zoneName = profile?.display_name || renderer.name || null;

  if (!profile) {
    return passthrough(streamUrl, sampleRate, bitDepth, formatId,
      'No zone profile — passthrough with defaults', zoneName);
  }

  const zoneMaxRate = profile.max_sample_rate;
  const zoneMaxDepth = profile.max_bit_depth;

  if (!zoneMaxRate || !zoneMaxDepth) {
    return passthrough(streamUrl, sampleRate, bitDepth, formatId,
      'Zone profile has no format limits — passthrough', zoneName);
  }

  const sourceExceedsRate = sampleRate > zoneMaxRate;
  const sourceExceedsDepth = bitDepth > zoneMaxDepth;

  if (!sourceExceedsRate && !sourceExceedsDepth) {
    return passthrough(streamUrl, sampleRate, bitDepth, formatId,
      `Source ${fmtRate(sampleRate)}/${bitDepth}-bit within zone max ` +
      `${fmtRate(zoneMaxRate)}/${zoneMaxDepth}-bit`,
      zoneName);
  }

  const targetRate = Math.min(sampleRate, zoneMaxRate);
  const targetDepth = Math.min(bitDepth, zoneMaxDepth);

  // TODO: When Connector-side downsampling supports target format params
  // via IPC, pass targetRate/targetDepth through the play_uri invoke.
  // For now the URL stays the same — the Connector may still downsample
  // based on its own renderer capability detection.
  return {
    action: 'downsample',
    originalUrl: streamUrl,
    effectiveUrl: streamUrl,
    originalFormat: { sampleRate, bitDepth, formatId },
    targetFormat: { sampleRate: targetRate, bitDepth: targetDepth },
    reason: `Downsampling from ${fmtRate(sampleRate)}/${bitDepth}-bit to ` +
      `${fmtRate(targetRate)}/${targetDepth}-bit for ${zoneName || 'zone'}`,
    zoneName,
    wasDownsampled: true,
  };
}

/** Multizone uses CD quality for broad device compatibility. */
export function getMultizoneFormatId(): number {
  return 6;
}

/**
 * Legacy helper based on zone profile max sample rate / bit depth.
 * Do NOT use for Qobuz stream URL selection — zone profiles can mis-state
 * caps (e.g. PMG showing CD limits). Playback must use getFormatIdForRenderer()
 * (Sonos → 6, else 27) in QueueController; routeStream still applies zone limits.
 *
 *   5  = MP3 320kbps
 *   6  = CD FLAC (16-bit / 44.1kHz)
 *   7  = MP3 variable
 *   27 = Hi-Res FLAC (up to 24-bit / 192kHz)
 */
export function getOptimalFormatId(zoneId?: string | null): number {
  const DEFAULT_HIRES = 27;
  const CD_QUALITY = 6;

  if (!zoneId) {
    const renderer = useMaestroStore.getState().activeConnectorRenderer;
    if (!renderer) return DEFAULT_HIRES;
    zoneId = renderer.id;
  }

  const profiles = useZoneProfileStore.getState().zones;
  const profile = profiles.find((p) => p.zone_id === zoneId);

  if (!profile) return DEFAULT_HIRES;

  const maxRate = profile.max_sample_rate;
  const maxDepth = profile.max_bit_depth;

  if (!maxRate || !maxDepth) return DEFAULT_HIRES;

  // If the zone can handle anything above CD quality, request hi-res.
  // Qobuz will serve the best available up to that limit.
  if (maxRate > 48000 || maxDepth > 16) return DEFAULT_HIRES;

  return CD_QUALITY;
}

/**
 * Build a human-readable label describing the format actually being
 * delivered to the renderer. Returns null if no track is playing.
 */
export function getDeliveredFormatLabel(): string | null {
  return getDeliveredFormatDisplay()?.label ?? null;
}

export interface DeliveredFormatDisplay {
  label: string;
  badgeKind: ZoneFormatBadgeKind;
}

export function getDeliveredFormatDisplay(): DeliveredFormatDisplay | null {
  const store = useMaestroStore.getState();
  const track = store.currentTrack;
  if (!track) return null;

  const networkLabel = getPlaybackNetworkFormatLabel();
  if (networkLabel) return { label: networkLabel, badgeKind: 'normal' };

  const fmt = (track.format as string) || 'FLAC';
  const bd = readBitDepth(track);
  const srKhz = readSampleRate(track);

  const { profile } = getActiveZoneProfile();
  if (!profile) {
    const quality = formatPlaybackQualityLabel(track);
    const label = quality ? `${fmt} · ${quality}` : fmt;
    return { label, badgeKind: 'normal' };
  }

  const { effectiveRateKhz, effectiveDepth } = deliveredPcmFormat(track, profile);
  const capped = (srKhz && effectiveRateKhz && effectiveRateKhz < srKhz) ||
    (bd && effectiveDepth && effectiveDepth < bd);

  const badgeKind = classifyZoneFormatBadge(profile, effectiveRateKhz, effectiveDepth, capped);

  if (badgeKind === 'untested_default') {
    return { label: UNTESTED_ZONE_BADGE_TEXT, badgeKind };
  }

  if (effectiveDepth && effectiveRateKhz) {
    const label = `${fmt} · ${effectiveDepth}/${effectiveRateKhz}`;
    return {
      label: capped ? `${label} (zone limit)` : label,
      badgeKind: capped ? 'verified_limit' : 'normal',
    };
  }
  const quality = formatPlaybackQualityLabel(track);
  const label = quality ? `${fmt} · ${quality}` : fmt;
  return { label, badgeKind: 'normal' };
}

/** Collapsed zone card summary on Devices page. */
export function getZoneFormatCeilingSummary(profile: ZoneProfile): {
  text: string;
  badgeKind: ZoneFormatBadgeKind;
} {
  if (isUntestedDefaultZoneCap(profile)) {
    return { text: UNTESTED_ZONE_BADGE_TEXT, badgeKind: 'untested_default' };
  }
  const rl = profile.max_sample_rate >= 1000
    ? `${(profile.max_sample_rate / 1000).toFixed(profile.max_sample_rate % 1000 ? 1 : 0)} kHz`
    : `${profile.max_sample_rate} Hz`;
  const bl = `${profile.max_bit_depth}-bit`;
  const dsd = profile.dsd_capability !== 'none' ? ` · ${profile.dsd_capability.toUpperCase()}` : '';
  const verified = profile.verified_at ? 'verified' : 'not tested';
  return {
    text: `${rl} / ${bl}${dsd} (${verified})`,
    badgeKind: profile.verified_at ? 'verified_limit' : 'normal',
  };
}
